import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError
} from '@reduxjs/toolkit/query'
import { clearLifetimeToken } from 'state/global/actions'

/**
 * Messages that mean the session itself is gone — not that this one request
 * went wrong.
 *
 * The distinction is the whole point. A token that the server has declared
 * dead will be dead on every future call, so keeping it means the app polls
 * forever with a credential that cannot work: measured over three days,
 * 118 rejected notification polls across eight people, each of them sitting in
 * a "signed in, nothing works" state with nothing on screen to say so.
 *
 * Any other 401 is left alone. A service restarting, a gateway hiccup or a
 * request that simply forgot its headers must not sign somebody out — logging
 * a person out over a transient fault is a worse failure than the one being
 * fixed, and it is the reason this matches on the message rather than on the
 * status.
 */
const SESSION_GONE = [
  'invalid session: session not found',
  'invalid token',
  'token does not match the account wallet'
]

export function isSessionGone(status: unknown, data: unknown): boolean {
  if (status !== 401) return false
  const message = (data as { message?: unknown } | null)?.message
  if (typeof message !== 'string') return false
  const normalised = message.toLowerCase()
  return SESSION_GONE.some((known) => normalised.includes(known))
}

/**
 * Wraps a base query so a dead session clears itself.
 *
 * The wallet comes from the request's own header rather than from the store:
 * it is the account this particular call was made for, which is the one whose
 * token the server just rejected. Reading "the current account" instead would
 * clear the wrong token whenever the two differ — exactly the situation a
 * wallet switch produces.
 */
export function withSessionRecovery(
  baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> {
  return async (args, api, extraOptions) => {
    const result = await baseQuery(args, api, extraOptions)
    const error = result.error as FetchBaseQueryError | undefined
    if (!error || !isSessionGone(error.status, error.data)) return result

    const headers = typeof args === 'string' ? undefined : args.headers
    const wallet = headerValue(headers, 'x-account-wallet')
    if (wallet) api.dispatch(clearLifetimeToken({ account: wallet }))
    return result
  }
}

function headerValue(headers: unknown, name: string): string | undefined {
  if (!headers) return undefined
  if (headers instanceof Headers) return headers.get(name) ?? undefined
  if (Array.isArray(headers)) {
    const hit = headers.find(([key]) => key.toLowerCase() === name)
    return hit?.[1]
  }
  const record = headers as Record<string, string>
  const key = Object.keys(record).find((k) => k.toLowerCase() === name)
  return key ? record[key] : undefined
}
